import { useState } from "react";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShoppingCart, Trash2 } from "lucide-react";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";
import { cn } from "@/lib/utils";

type Purchase = {
  id: string;
  user_id: string;
  content_type: string;
  content_id: string;
  price: number;
  purchased_at: string;
  order_id?: string;
  karma_profiles?: {
    display_name?: string;
  };
  customer?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    identityNumber?: string;
    addressLine1?: string;
    city?: string;
    district?: string;
  };
};

type PurchasesResponse = { ok: true; items: Purchase[] };

async function fetchPurchases(): Promise<Purchase[]> {
  const res = await adminFetch("/api/admin/content-purchases");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Satın almalar alınamadı.");
  return Array.isArray((json as PurchasesResponse)?.items) ? (json as PurchasesResponse).items : [];
}

async function deletePurchase(id: string): Promise<void> {
  const res = await adminFetch(`/api/admin/content-purchases/${encodeURIComponent(id)}`, { method: "DELETE" });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Kayıt silinemedi.");
}

function formatDate(iso: string) {
  try {
    return new Intl.DateTimeFormat("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatTry(n: number) {
  try {
    return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${n} ₺`;
  }
}

const CONTENT_TYPE_LABELS: Record<string, string> = {
  meditation: "Meditasyon",
  breath: "Nefes",
  frekans: "Frekans",
};

const CONTENT_TYPE_COLORS: Record<string, string> = {
  meditation: "bg-violet-500/10 text-violet-400",
  breath: "bg-blue-500/10 text-blue-400",
  frekans: "bg-emerald-500/10 text-emerald-400",
};

type FilterType = "all" | "meditation" | "breath" | "frekans";

export default function AdminContentPurchases() {
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";
  const { sessionQuery, loginMutation } = useAdminSession();
  const authenticated = Boolean(sessionQuery.data?.authenticated);
  const configured = Boolean(sessionQuery.data?.configured);
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState<FilterType>("all");
  const [search, setSearch] = useState("");

  const deleteMutation = useMutation({
    mutationFn: deletePurchase,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-content-purchases"] }),
  });

  const purchasesQuery = useQuery({
    queryKey: ["admin-content-purchases"],
    queryFn: fetchPurchases,
    enabled: authenticated,
    staleTime: 30_000,
  });

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.error?.message}
      />
    );
  }

  const items = purchasesQuery.data ?? [];

  const filtered = items.filter((p) => {
    if (filter !== "all" && p.content_type !== filter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const name = (p.karma_profiles?.display_name ?? p.user_id).toLowerCase();
      const customerName = [p.customer?.firstName, p.customer?.lastName].filter(Boolean).join(" ").toLowerCase();
      const customerEmail = (p.customer?.email ?? "").toLowerCase();
      const orderId = (p.order_id ?? "").toLowerCase();
      if (!name.includes(q) && !p.content_id.toLowerCase().includes(q) && !customerName.includes(q) && !customerEmail.includes(q) && !orderId.includes(q)) return false;
    }
    return true;
  });

  const totalRevenue = filtered.reduce((sum, p) => sum + (p.price ?? 0), 0);

  const counts = {
    all: items.length,
    meditation: items.filter((p) => p.content_type === "meditation").length,
    breath: items.filter((p) => p.content_type === "breath").length,
    frekans: items.filter((p) => p.content_type === "frekans").length,
  };

  return (
    <div className={cn("flex min-h-screen", theme === "dark" ? "bg-slate-950 text-white" : "bg-slate-50 text-slate-900")}>
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex-1 overflow-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-inherit px-6 py-4">
          <NumerolojiAdminMobileSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />
          <div className="flex items-center gap-3">
            <ShoppingCart className="h-5 w-5 text-violet-400" />
            <h1 className="text-lg font-bold">Dijital İçerik Satışları</h1>
          </div>
          <div className="text-sm font-semibold text-emerald-400">
            Toplam: {formatTry(totalRevenue)}
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Filtre + Arama */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-2">
              {(["all", "meditation", "breath", "frekans"] as FilterType[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={cn(
                    "rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors",
                    filter === f
                      ? "border-violet-500 bg-violet-500/20 text-violet-300"
                      : "border-slate-700 text-slate-400 hover:border-slate-500",
                  )}
                >
                  {f === "all" ? "Tümü" : CONTENT_TYPE_LABELS[f]} ({counts[f]})
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Kullanıcı veya içerik ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={cn(
                "h-9 rounded-lg border px-3 text-sm outline-none focus:ring-2 focus:ring-violet-500/30",
                theme === "dark"
                  ? "border-slate-700 bg-slate-800 text-slate-100 placeholder:text-slate-500"
                  : "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400",
              )}
            />
          </div>

          {/* Tablo */}
          {purchasesQuery.isLoading ? (
            <div className="py-12 text-center text-sm text-slate-400">Yükleniyor...</div>
          ) : purchasesQuery.isError ? (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
              {(purchasesQuery.error as Error).message}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400">Kayıt bulunamadı.</div>
          ) : (
            <div className={cn("overflow-hidden rounded-xl border", theme === "dark" ? "border-slate-800" : "border-slate-200")}>
              <table className="w-full text-sm">
                <thead className={cn("text-xs uppercase tracking-wider", theme === "dark" ? "bg-slate-800/60 text-slate-400" : "bg-slate-100 text-slate-500")}>
                  <tr>
                    <th className="px-4 py-3 text-left">Kullanıcı</th>
                    <th className="px-4 py-3 text-left">Müşteri</th>
                    <th className="px-4 py-3 text-left">İçerik Türü</th>
                    <th className="px-4 py-3 text-left">İçerik ID</th>
                    <th className="px-4 py-3 text-left">Sipariş</th>
                    <th className="px-4 py-3 text-right">Fiyat</th>
                    <th className="px-4 py-3 text-left">Tarih</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {filtered.map((p) => {
                    const name = p.karma_profiles?.display_name ?? p.user_id.slice(0, 8) + "…";
                    const typeLabel = CONTENT_TYPE_LABELS[p.content_type] ?? p.content_type;
                    const typeColor = CONTENT_TYPE_COLORS[p.content_type] ?? "bg-slate-500/10 text-slate-400";
                    return (
                      <tr
                        key={p.id}
                        className={cn(
                          "transition-colors",
                          theme === "dark" ? "hover:bg-slate-800/40" : "hover:bg-slate-50",
                        )}
                      >
                        <td className="px-4 py-3 font-medium">{name}</td>
                        <td className="px-4 py-3 text-xs text-slate-400">
                          {p.customer ? (
                            <div className="space-y-0.5">
                              <div className="font-medium text-slate-300">{[p.customer.firstName, p.customer.lastName].filter(Boolean).join(" ") || "—"}</div>
                              {p.customer.email && <div className="text-slate-500">{p.customer.email}</div>}
                              {p.customer.phone && <div className="text-slate-500">📞 {p.customer.phone}</div>}
                              {p.customer.identityNumber && <div className="text-slate-500">TC: {p.customer.identityNumber}</div>}
                              {(p.customer.addressLine1 || p.customer.city) && (
                                <div className="text-slate-500">
                                  {[p.customer.addressLine1, p.customer.district, p.customer.city].filter(Boolean).join(", ")}
                                </div>
                              )}
                            </div>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold", typeColor)}>
                            {typeLabel}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-400">{p.content_id}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">
                          {p.order_id ? p.order_id.slice(0, 16) + "…" : "—"}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-emerald-400">
                          {p.price > 0 ? formatTry(p.price) : "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-400">{formatDate(p.purchased_at)}</td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            disabled={deleteMutation.isPending}
                            onClick={() => {
                              if (!window.confirm(`"${p.karma_profiles?.display_name ?? p.user_id}" kullanıcısının "${CONTENT_TYPE_LABELS[p.content_type] ?? p.content_type}" satın alımı silinecek. Onaylıyor musun?`)) return;
                              deleteMutation.mutate(p.id);
                            }}
                            className="flex items-center gap-1 rounded-lg border border-rose-500/30 bg-rose-500/10 px-2.5 py-1 text-xs font-semibold text-rose-400 transition-colors hover:bg-rose-500/20 disabled:opacity-50"
                          >
                            <Trash2 className="h-3 w-3" />
                            İptal
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="text-xs text-slate-500">{filtered.length} kayıt gösteriliyor</div>
        </div>
      </div>
    </div>
  );
}
